import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Bell,
  Briefcase,
  CircleUserRound,
  PackageSearch,
  Plus,
  Search,
  Star,
} from "lucide-react";
import styled, { createGlobalStyle, css, keyframes } from "styled-components";
import {
  getMarketplaceProfile,
  getPublicProfiles,
  MarketplaceProfile,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { resolveImage } from "../../../Utils/helper";
import { MARKETPLACE_FONTS } from "./MarketplaceConstants";
import team2 from "../../../assets/images/marketplace/team2.webp";
import team3 from "../../../assets/images/marketplace/team3.webp";
import team4 from "../../../assets/images/marketplace/team4.webp";
import team5 from "../../../assets/images/marketplace/team5.webp";

const PlusJakartaSans = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

const Page = styled.main`
  min-height: 100vh;
  background: var(--mp-bodyBackground);
  color: var(--mp-headingColor);
  font-family: "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
`;

const TopPanel = styled.section`
  position: relative;
  overflow: hidden;
  background: var(--mp-background);
  padding: 24px 0 80px;

  &::before {
    content: "";
    position: absolute;
    right: -170px;
    top: -180px;
    width: 620px;
    height: 920px;
    border-radius: 999px;
    background: linear-gradient(118deg, rgba(255, 201, 81, 0.2), rgba(133, 7, 111, 0.4) 50%, rgba(133, 7, 111, 0));
    filter: blur(32px);
    opacity: 0.42;
    transform: rotate(12deg);
  }

  &::after {
    content: "";
    position: absolute;
    left: -150px;
    bottom: 120px;
    width: 420px;
    height: 700px;
    border-radius: 999px;
    background: linear-gradient(62deg, rgba(98, 225, 255, 0.24), rgba(255, 173, 226, 0.32) 52%, rgba(255, 173, 226, 0));
    filter: blur(36px);
    opacity: 0.16;
  }
`;

const Container = styled.div`
  width: min(1120px, calc(100% - 48px));
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: 720px) {
    width: min(100% - 32px, 1120px);
  }
`;

const Hero = styled.div`
  padding: 56px 0 80px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 48px;
  animation: ${fadeUp} 0.45s ease both;

  @media (max-width: 860px) {
    align-items: flex-start;
    flex-direction: column;
    padding: 36px 0 44px;
  }
`;

const HeroCopy = styled.div`
  max-width: 672px;
`;

const HeroTitle = styled.h1`
  margin: 0 0 8px;
  font-size: 48px;
  line-height: 56px;
  font-weight: 700;
  color: var(--mp-heroHeadingColor);

  span {
    display: block;
    background: linear-gradient(90deg, #ffdea1 0%, #ffd8ee 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  @media (max-width: 720px) {
    font-size: 36px;
    line-height: 42px;
  }
`;

const HeroText = styled.p`
  margin: 0;
  color: var(--mp-heroSubColor);
  font-size: 18px;
  line-height: 28px;
  max-width: 672px;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
`;

const PillButton = styled.button<{ $primary?: boolean }>`
  min-height: 44px;
  border: 1px solid ${p => (p.$primary ? "transparent" : "var(--mp-sidebarBorder)")};
  border-radius: 999px;
  padding: 12px 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  font: 600 14px/20px "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  color: ${p => (p.$primary ? "#261900" : "var(--mp-headingColor)")};
  background: ${p => (p.$primary ? "linear-gradient(90deg, #ffdea1 0%, #ffade2 100%)" : "var(--mp-cardBackground)")};
  box-shadow: 0 4px 12px rgba(0,0,0,0.14);
  transition: transform 0.18s ease, opacity 0.18s ease;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }
`;

const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.article<{ $wide?: boolean }>`
  position: relative;
  overflow: hidden;
  min-height: 506px;
  grid-column: ${p => (p.$wide ? "span 8" : "span 4")};
  border-radius: 32px;
  background: var(--mp-cardBackground);
  border: 1px solid var(--mp-sidebarBorder);
  padding: 48px;
  box-shadow: 0 4px 18px rgba(0,0,0,0.12);
  animation: ${fadeUp} 0.5s ease both;

  ${p => p.$wide && css`
    &::after {
      content: "";
      position: absolute;
      right: 0;
      top: 0;
      width: 256px;
      height: 256px;
      border-bottom-left-radius: 999px;
      background: linear-gradient(225deg, rgba(255, 222, 161, 0.14), rgba(255, 222, 161, 0));
    }
  `}

  @media (max-width: 980px) {
    grid-column: 1;
  }

  @media (max-width: 640px) {
    min-height: auto;
    border-radius: 24px;
    padding: 24px;
  }
`;

const PanelHeader = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 48px;
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBubble = styled.div<{ $accent?: "pink" | "blue" }>`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: ${p => (p.$accent === "blue" ? "#62e1ff" : "#85076f")};
  color: ${p => (p.$accent === "blue" ? "#0c0e10" : "#ffade2")};
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  color: var(--mp-headingColor);
`;

const TextLink = styled.button`
  border: 0;
  background: transparent;
  color: #ffebc9;
  padding: 0;
  font: 600 14px/20px "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  cursor: pointer;
`;

const HiringList = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HiringRow = styled.div`
  min-height: 96px;
  border-radius: 48px;
  background: color-mix(in srgb, var(--mp-cardBackground) 86%, var(--mp-background));
  border: 1px solid var(--mp-sidebarBorder);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px;

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
    border-radius: 28px;
  }
`;

const HiringInfo = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 24px;
`;

const SoftIcon = styled.div`
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 32px;
  background: color-mix(in srgb, var(--mp-cardBackground) 78%, var(--mp-headingColor));
  color: var(--mp-subColor);
  display: grid;
  place-items: center;
`;

const RowTitle = styled.h3`
  margin: 0;
  color: var(--mp-headingColor);
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
`;

const RowMeta = styled.p`
  margin: 0;
  color: var(--mp-subColor);
  font-size: 14px;
  line-height: 20px;
`;

const HiringRight = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
`;

const AvatarStack = styled.div`
  display: flex;
`;

const MiniAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  object-fit: cover;
  border: 2px solid var(--mp-cardBackground);
  margin-right: -8px;
  background: var(--mp-cardBackground);
`;

const AvatarMore = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  color: var(--mp-subColor);
  background: color-mix(in srgb, var(--mp-cardBackground) 78%, var(--mp-headingColor));
  border: 2px solid var(--mp-cardBackground);
  font-size: 12px;
  font-weight: 500;
`;

const Badge = styled.span<{ $warm?: boolean }>`
  border-radius: 999px;
  padding: 4px 12px;
  background: ${p => (p.$warm ? "rgba(255,222,161,0.2)" : "color-mix(in srgb, var(--mp-cardBackground) 78%, var(--mp-headingColor))")};
  color: ${p => (p.$warm ? "#ffdea1" : "var(--mp-subColor)")};
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  white-space: nowrap;
`;

const DonutArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 34px;
`;

const DonutWrap = styled.div`
  width: 126px;
  height: 126px;
  position: relative;
`;

const DonutLabel = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
`;

const DonutCount = styled.strong`
  display: block;
  color: var(--mp-headingColor);
  font-size: 24px;
  line-height: 28px;
`;

const DonutSub = styled.span`
  color: var(--mp-subColor);
  font-size: 10px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
`;

const EquipmentList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EquipmentItem = styled.div`
  min-height: 36px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--mp-cardBackground) 86%, var(--mp-background));
  border: 1px solid var(--mp-sidebarBorder);
  color: var(--mp-subColor);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px 8px 16px;
  font-size: 12px;
  line-height: 16px;
`;

const Section = styled.section`
  background: var(--mp-bodyBackground);
  padding: 80px 0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 8px;
  color: var(--mp-headingColor, #ffffff);
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const SectionSub = styled.p`
  margin: 0;
  color: #D2C5AF;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
`;

const InlineLink = styled.button`
  border: 0;
  background: transparent;
  color: #D2C5AF;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  cursor: pointer;
  font: 600 14px/20px "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const TalentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const TalentCard = styled.button`
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0;
  text-align: left;
  cursor: pointer;
  color: inherit;
  background: #121417;
  border-radius: 20px;
  overflow: hidden;
  min-width: 0;
  height: 370px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.5);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const TalentImageWrap = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #1a1d21;
`;

const TalentImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s ease;

  ${TalentCard}:hover & {
    transform: scale(1.05);
  }
`;

const TalentOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.92) 0%,
    rgba(0, 0, 0, 0.65) 40%,
    rgba(0, 0, 0, 0.15) 70%,
    rgba(0, 0, 0, 0.05) 100%
  );
  pointer-events: none;
`;

const TalentPlaceholder = styled.div`
  height: 100%;
  display: grid;
  place-items: center;
  color: var(--mp-subColor);
  background: linear-gradient(135deg, var(--mp-cardBackground), var(--mp-bodyBackground));
`;

const TalentBody = styled.div`
  position: relative;
  z-index: 2;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Rating = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  margin-bottom: 2px;
`;

const ReviewsCount = styled.span`
  color: rgba(255, 255, 255, 0.65);
  font-weight: 400;
  font-size: 13px;
`;

const TalentName = styled.h3`
  margin: 0;
  color: #ffffff;
  font-size: 20px;
  line-height: 26px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const TalentRole = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
`;

const SkeletonBase = css`
  background: linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.1), rgba(255,255,255,0.05));
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`;

const SkeletonCard = styled.div`
  height: 370px;
  border-radius: 20px;
  ${SkeletonBase}
`;

const SearchStrip = styled.div`
  display: none;

  @media (max-width: 760px) {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 24px;
    border-radius: 999px;
    background: var(--mp-cardBackground);
    color: var(--mp-subColor);
    padding: 12px 18px;
    font-size: 14px;
  }
`;

const HIRING_ITEMS = [
  {
    title: "Director of Photography",
    meta: "\"Neon Nights\" Short Film - Los Angeles",
    applicants: "6 Applicants",
    avatars: [team2, team3],
    more: "+4",
  },
  {
    title: "Lead Wardrobe Stylist",
    meta: "Commercial Shoot - Remote Prep",
    applicants: "1 Applicant",
    avatars: [team4],
  },
];

const EQUIPMENT_ITEMS = [
  { name: "ARRI Alexa Mini LF", pending: false },
  { name: "Cooke Anamorphic Set", pending: false },
  { name: "Astraera Titan Tubes", pending: true },
];

const FALLBACK_TALENT = [
  { name: "Maya Lin", role: "Cinematographer", image: team2, rating: "4.9", reviewsCount: 42 },
  { name: "David Chen", role: "Production Sound Mixer", image: team3, rating: "5.0", reviewsCount: 18 },
  { name: "Sarah Jenkins", role: "Production Designer", image: team4, rating: "4.8", reviewsCount: 55 },
  { name: "Marcus Thorne", role: "Gaffer / Chief Lighting", image: team5, rating: "4.9", reviewsCount: 30 },
];

const DonutChart: React.FC<{ value: number; max: number }> = ({ value, max }) => {
  const pct = value / max;
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pct);

  return (
    <DonutWrap>
      <svg width="126" height="126" viewBox="0 0 126 126">
        <circle cx="63" cy="63" r={radius} fill="none" stroke="rgba(160,160,160,0.2)" strokeWidth="12" />
        <circle
          cx="63"
          cy="63"
          r={radius}
          fill="none"
          stroke="url(#equipmentDonut)"
          strokeWidth="12"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 63 63)"
        />
        <defs>
          <linearGradient id="equipmentDonut" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffade2" />
            <stop offset="100%" stopColor="#ffdea1" />
          </linearGradient>
        </defs>
      </svg>
      <DonutLabel>
        <div>
          <DonutCount>{value}/{max}</DonutCount>
          <DonutSub>Secured</DonutSub>
        </div>
      </DonutLabel>
    </DonutWrap>
  );
};

const MarketplaceHomeDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<MarketplaceProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const talentPerPage = 4;

  const [userName, setUserName] = useState<string>(() => {
    try {
      const mp = localStorage.getItem("marketplace_profile");
      if (mp) {
        const parsed = JSON.parse(mp);
        if (parsed?.display_name) return parsed.display_name;
      }
      const ui = localStorage.getItem("user_info");
      if (ui) {
        const parsed = JSON.parse(ui);
        return parsed?.name || parsed?.username || "";
      }
    } catch {
      return "";
    }
    return "";
  });

  const fetchProfiles = useCallback(async () => {
    setProfilesLoading(true);
    try {
      const res = await getPublicProfiles({ page: 1, page_size: 20, profile_type: "freelancer" });
      setProfiles(res.results ?? []);
    } catch {
      setProfiles([]);
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
    getMarketplaceProfile()
      .then(res => {
        const name = res?.data?.display_name;
        if (name) setUserName(name);
      })
      .catch(() => {});
  }, [fetchProfiles]);

  const visibleProfiles = profiles.slice(0, talentPerPage);
  const showFallbackTalent = !profilesLoading && visibleProfiles.length === 0;

  return (
    <Page>
      <PlusJakartaSans />
      <TopPanel>
        <Container>
          <SearchStrip>
            <Search size={15} />
            Search CreativeSpace...
          </SearchStrip>
          <Hero>
            <HeroCopy>
              <HeroTitle>
                Welcome back,
                <span>{userName || "Elena Sterling"}</span>
              </HeroTitle>
              <HeroText>
                Your production pipeline is running smoothly. You have 3 active listings and 2 pending equipment rentals this week.
              </HeroText>
            </HeroCopy>
            <HeroActions>
              <PillButton onClick={() => navigate("/marketplace/post-requirement")}>
                <Plus size={14} />
                New Project
              </PillButton>
              <PillButton $primary onClick={() => navigate("/marketplace/sell")}>
                Post Listing
              </PillButton>
            </HeroActions>
          </Hero>

          <BentoGrid>
            <Panel $wide>
              <PanelHeader>
                <TitleWrap>
                  <IconBubble>
                    <Briefcase size={18} />
                  </IconBubble>
                  <PanelTitle>Active Hiring</PanelTitle>
                </TitleWrap>
                <TextLink onClick={() => navigate("/marketplace/project-quote")}>View All</TextLink>
              </PanelHeader>
              <HiringList>
                {HIRING_ITEMS.map(item => (
                  <HiringRow key={item.title}>
                    <HiringInfo>
                      <SoftIcon>
                        <Briefcase size={18} />
                      </SoftIcon>
                      <div>
                        <RowTitle>{item.title}</RowTitle>
                        <RowMeta>{item.meta}</RowMeta>
                      </div>
                    </HiringInfo>
                    <HiringRight>
                      <AvatarStack>
                        {item.avatars.map(avatar => (
                          <MiniAvatar key={avatar} src={avatar} alt="" />
                        ))}
                        {item.more && <AvatarMore>{item.more}</AvatarMore>}
                      </AvatarStack>
                      <Badge $warm={item.applicants !== "1 Applicant"}>{item.applicants}</Badge>
                    </HiringRight>
                  </HiringRow>
                ))}
              </HiringList>
            </Panel>

            <Panel>
              <PanelHeader>
                <TitleWrap>
                  <IconBubble $accent="blue">
                    <PackageSearch size={18} />
                  </IconBubble>
                  <PanelTitle>Equipment</PanelTitle>
                </TitleWrap>
              </PanelHeader>
              <DonutArea>
                <DonutChart value={3} max={4} />
                <EquipmentList>
                  {EQUIPMENT_ITEMS.map(item => (
                    <EquipmentItem key={item.name}>
                      {item.name}
                      {item.pending ? <Badge>Pending</Badge> : <Bell size={12} color="#ffebc9" />}
                    </EquipmentItem>
                  ))}
                </EquipmentList>
              </DonutArea>
            </Panel>
          </BentoGrid>
        </Container>
      </TopPanel>

      <Section>
        <Container>
          <SectionHeader>
            <div>
              <SectionTitle>Trending Talent</SectionTitle>
              <SectionSub>Top rated professionals available for booking this month.</SectionSub>
            </div>
            <InlineLink onClick={() => navigate("/marketplace/talent")}>
              Explore Directory <ArrowRight size={13} />
            </InlineLink>
          </SectionHeader>

          <TalentGrid>
            {profilesLoading
              ? Array.from({ length: talentPerPage }).map((_, i) => <SkeletonCard key={i} />)
              : showFallbackTalent
                ? FALLBACK_TALENT.map(item => (
                    <TalentCard key={item.name} onClick={() => navigate("/marketplace/talent")}>
                      <TalentImageWrap>
                        <TalentImage src={item.image} alt={item.name} />
                        <TalentOverlay />
                      </TalentImageWrap>
                      <TalentBody>
                        <Rating>
                          <Star size={13} fill="#ffdea1" color="#ffdea1" />
                          <span>{item.rating}</span>
                          <ReviewsCount>({item.reviewsCount})</ReviewsCount>
                        </Rating>
                        <TalentName>{item.name}</TalentName>
                        <TalentRole>{item.role}</TalentRole>
                      </TalentBody>
                    </TalentCard>
                  ))
                : visibleProfiles.map(profile => {
                    const img = profile.profile_picture ? resolveImage(profile.profile_picture) : "";
                    const rating = profile.profile_avg_rating?.toFixed(1) ?? "4.9";
                    const reviewsCount = profile.profile_total_reviews ?? 42;
                    return (
                      <TalentCard key={profile.id} onClick={() => navigate(`/marketplace/profile/${profile.id}`)}>
                        <TalentImageWrap>
                          {img ? (
                            <TalentImage src={img} alt={profile.display_name} />
                          ) : (
                            <TalentPlaceholder>
                              <CircleUserRound size={48} />
                            </TalentPlaceholder>
                          )}
                          <TalentOverlay />
                        </TalentImageWrap>
                        <TalentBody>
                          <Rating>
                            <Star size={13} fill="#ffdea1" color="#ffdea1" />
                            <span>{rating}</span>
                            <ReviewsCount>({reviewsCount})</ReviewsCount>
                          </Rating>
                          <TalentName>{profile.display_name}</TalentName>
                          <TalentRole>{profile.profession || profile.profile_type || "Creative Professional"}</TalentRole>
                        </TalentBody>
                      </TalentCard>
                    );
                  })}
          </TalentGrid>
        </Container>
      </Section>
    </Page>
  );
};

export default MarketplaceHomeDashboardPage;
